import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { usePermissions } from '@tyro/api';
import {
  FloatCardDraggableContainer,
  FloatingCard,
  useFloatCardPositioning,
} from '@tyro/core/floating-card';
import { Scrollbar } from '@tyro/core/scrollbar';
import { useTranslation } from '@tyro/i18n';
import { useEffect } from 'react';
import {
  useReaderActions,
  useReaderState,
} from '../../store/documentation-reader-provider';
import { ReaderBreadcrumb } from './reader-breadcrumb';
import { ReaderContent } from './reader-content';
import { ReaderHeader } from './reader-header';
import { ReaderPageList } from './reader-page-list';
import { ReaderSearch } from './reader-search';

// Defaults for the floating shell. Wider than 800px so code blocks and tables
// read comfortably, narrower than 1000px so the app underneath stays visible
// alongside. Height fills the available viewport.
const FLOATING_WIDTH = 960;
const FLOATING_MARGIN = 8;

// Drives the card's bounds from the full-screen toggle without remounting
// the FloatingCard. Lives inside FloatingCard so it can read the positioning
// context; remounting (via a `key`) would replay the slide-in animation.
function ReaderBoundsSync() {
  const { setSize } = useFloatCardPositioning();
  const { isFullScreen } = useReaderState();

  useEffect(() => {
    if (isFullScreen) {
      setSize({
        x: 0,
        y: 0,
        width: window.innerWidth,
        height: window.innerHeight,
      });
      return;
    }
    const width = Math.min(
      FLOATING_WIDTH,
      window.innerWidth - FLOATING_MARGIN * 2,
    );
    const height = window.innerHeight - FLOATING_MARGIN * 2;
    setSize({
      width,
      height,
      x: window.innerWidth - width - FLOATING_MARGIN,
      y: FLOATING_MARGIN,
    });
  }, [isFullScreen, setSize]);

  return null;
}

export function DocumentationReader() {
  const { t } = useTranslation(['documentation']);
  const { isStaffUserWithPermission } = usePermissions();

  const { close } = useReaderActions();
  const { isOpen, view, spaces, isLoadingSpaces } = useReaderState();

  const hasNoContent = (spaces ?? []).length === 0;

  if (!isStaffUserWithPermission('ps:1:documentation:read_documentation')) {
    return null;
  }

  return (
    <FloatingCard
      open={isOpen}
      onClose={close}
      initialWidth={FLOATING_WIDTH}
      initialHeight={window.innerHeight}
      initialPosition={{ bottom: FLOATING_MARGIN, right: FLOATING_MARGIN }}
      anchor="right"
      CardProps={{
        role: 'complementary',
        'aria-label': t('documentation:reader.title'),
        sx: {
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(20px)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
    >
      <ReaderBoundsSync />
      <Stack sx={{ height: '100%' }}>
        <FloatCardDraggableContainer>
          <ReaderHeader />
        </FloatCardDraggableContainer>

        {isLoadingSpaces && (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ flex: 1, py: 6 }}
          >
            <CircularProgress size={28} />
          </Stack>
        )}

        {!isLoadingSpaces && hasNoContent && (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ flex: 1, px: 3, py: 6 }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: 'center' }}
            >
              {t('documentation:reader.noArticles')}
            </Typography>
          </Stack>
        )}

        {!isLoadingSpaces && !hasNoContent && view === 'home' && (
          <ReaderSearch />
        )}

        {!isLoadingSpaces && !hasNoContent && view !== 'home' && (
          <>
            <ReaderBreadcrumb />
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <Scrollbar style={{ height: '100%' }}>
                {view === 'space' && <ReaderPageList />}
                {view === 'page' && <ReaderContent />}
              </Scrollbar>
            </Box>
          </>
        )}
      </Stack>
    </FloatingCard>
  );
}
